<template>
  <div class="min-h-screen flex flex-col">
    <!-- Skip link: first focusable element, jumps keyboard users past the nav to <main>. -->
    <a
      href="#main"
      class="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[1000] focus:rounded focus:bg-white focus:px-4 focus:py-2 focus:text-unify-blue-700 focus:shadow-lg focus:outline focus:outline-2 focus:outline-unify-blue-700"
    >
      {{ t('common.skipToContent') }}
    </a>
    <div
      id="app-status-announcer"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      class="sr-only"
    />
    <Toast />
    <ConfirmDialog
      :header="t('common.confirm')"
      :accept-label="t('common.yes')"
      :reject-label="t('common.no')"
      :style="{ width: '30vw' }"
    />

    <!-- Global Navigation - renders on all pages that need it -->
    <!-- No <nav> wrapper: NavigationBar is itself the Primary landmark, and nesting them
         gave every authenticated page two identically-named ones (1.3.1). -->
    <header v-if="shouldShowNavigation">
      <NavigationBar />
    </header>
    <header v-if="!shouldShowNavigation && shouldShowLogoNav">
      <NavigationBarLogoOnly />
    </header>

    <!-- Grows to fill any leftover viewport height so the footer sits at the bottom
         of the screen on short pages instead of trailing off after a tall blank gap. -->
    <div class="flex-1 flex flex-col">
      <router-view />
    </div>

    <AppFooter v-if="shouldShowFooter" />
  </div>
</template>

<script setup lang="ts">
import { inject, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import NavigationBar from '@/components/navigation/NavigationBar.vue';
import AppFooter from '@/components/navigation/AppFooter.vue';
import { loadDevSessionFromEnv } from './services/GeneralService';
import { AuthStore } from '@/stores/auth';

const t: any = inject('t');
const route = useRoute();

/**
 * Block to run local logic for development environment
 */
try {
  if (import.meta.env.DEV) {
    loadDevSessionFromEnv();
  }
} catch (error) {
  console.log('Dev error: ', error);
}

// Centralized logic for when to show navigation
const shouldShowNavigation = computed(() => {
  // Return false immediately if no route name (during initial load/navigation)
  if (!route.name) return false;

  // Don't show navigation if explicitly disabled in route meta
  if (route.meta.renderNav === false) return false;

  // Routes that are public but sit inside the app show nav only once there is a session.
  if (route.meta.navRequiresAuth === true && !AuthStore().getIsLoggedIn) return false;

  // Don't show navigation on auth pages, landing pages, etc.
  const hideNavOnRoutes = ['auth-home', 'register-home', 'landing-home', 'challenge-view', 'challenge-home'];

  if (hideNavOnRoutes.includes(route.name as string)) return false;

  // Show navigation on all other authenticated pages
  return true;
});

const shouldShowLogoNav = computed(() => {
  // Return false immediately if no route name (during initial load/navigation)
  if (!route.name) return false;

  // Don't show logo navigation if explicitly disabled in route meta
  if (route.meta.renderLogoNav === false) return false;

  // Show logo navigation on specific routes. These are the public views, where the logo is the
  // only chrome that makes sense: it says whose product this is without offering routes into it.
  const showLogoNavOnRoutes = ['insights-shared', 'accessibility'];

  return showLogoNavOnRoutes.includes(route.name as string);
});

// Don't show the global footer if explicitly disabled in route meta.
const shouldShowFooter = computed(() => route.meta.renderFooter !== false);

onMounted(() => {
  const authStore = AuthStore();
  const user = authStore.getUser;

  if (authStore.getIsLoggedIn) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'user_authenticated',
      isEducator: !!user.orgContactId,
      isAdmin: user.orgContactRole === 'admin',
      isStaff: user.isStaff,
      isSuperuser: user.isSuperuser,
    });
  }
});
</script>
